"use client";

import React from "react";
import { ShieldCheck } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import { Card, CardContent, CardFooter } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
const ShopifyProductsProvider: React.FC<{
  children: React.ReactNode;
  domain: string;
  limit: number;
}> = ({
  children
}) => <>{children}</>;
const useShopifyProducts = () => ({
  products: [],
  loading: false,
  error: null
});
const AddToCartButton: React.FC<{
  variantId: string;
  className: string;
  buttonClassName: string;
  text: string;
  showQuantity: boolean;
}> = ({
  buttonClassName,
  text
}) => <button className={buttonClassName}>{text}</button>;
function FeaturedProductsCarousel() {
  const {
    products,
    loading,
    error
  } = useShopifyProducts();
  if (loading) {
    return <div className="w-full flex gap-6 overflow-hidden">
        {[...Array(4)].map((_, i) => <div key={i} data-index={i} className="h-[400px] w-full min-w-[280px] sm:min-w-[320px] rounded-2xl bg-muted/30 animate-pulse shrink-0" />)}
      </div>;
  }
  if (error) {
    return <div className="text-center py-12 text-muted-foreground font-default">
        Unable to load wellness products at this time.
      </div>;
  }
  if (!products || products.length === 0) {
    return null;
  }
  return <Carousel opts={{
    align: "start",
    loop: true
  }} className="w-full">
      <CarouselContent className="-ml-4 md:-ml-6">
        {products.map((product: any, i: number) => <CarouselItem key={product.id} data-index={i} className="pl-4 md:pl-6 basis-full sm:basis-1/2 lg:basis-1/3 xl:basis-1/4">
            <Card className="h-full flex flex-col overflow-hidden border-border/50 shadow-sm hover:shadow-xl transition-all duration-500 group bg-card rounded-lg">
              <div className="relative aspect-square overflow-hidden bg-muted/5">
                <Badge variant="secondary" className="absolute top-3 left-3 z-10 bg-background/80 backdrop-blur-md text-foreground border-none shadow-sm gap-1.5 px-2 py-1 rounded-sm text-[10px] uppercase tracking-widest font-default">
                  <ShieldCheck className="w-3 h-3" />
                  M / O Certified
                </Badge>
                {product.featured_image_url ? <img src={product.featured_image_url} alt={product.title} className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105" loading="lazy" /> : <div className="w-full h-full flex items-center justify-center text-muted-foreground bg-muted/10 font-default text-xs">
                    Image Pending
                  </div>}
              </div>

              <CardContent className="p-5 flex-grow flex flex-col">
                <Link to={product.link} className="block mb-2 group-hover:opacity-70 transition-opacity">
                  <h3 className="text-sm font-medium font-default text-foreground line-clamp-2 uppercase tracking-wide">
                    {product.title}
                  </h3>
                </Link>
                <div className="mt-auto pt-4 flex items-center justify-between">
                  <div className="text-sm font-medium font-default text-muted-foreground">
                    ${product.price?.toFixed(2)}
                  </div>
                </div>
              </CardContent>

              <CardFooter className="px-5 pb-5 pt-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <AddToCartButton variantId={product.variants?.[0]?.id || ""} className="w-full" buttonClassName="bg-primary text-primary-foreground hover:bg-primary/90 w-full rounded-md py-4 text-xs uppercase tracking-widest font-default transition-all shadow-md" text="Quick Add" showQuantity={false} />
              </CardFooter>
            </Card>
          </CarouselItem>)}
      </CarouselContent>

      <div className="flex items-center justify-end gap-3 mt-8 pr-4">
        <CarouselPrevious className="relative inset-0 translate-x-0 translate-y-0 h-10 w-10 bg-background border-border hover:bg-accent hover:text-foreground shadow-none rounded-full transition-colors" />
        <CarouselNext className="relative inset-0 translate-x-0 translate-y-0 h-10 w-10 bg-background border-border hover:bg-accent hover:text-foreground shadow-none rounded-full transition-colors" />
      </div>
    </Carousel>;
}
export default function FeaturedWellnessProducts() {
  return <section data-section-id="222408" id="featured-wellness-products" className="bg-background text-foreground py-20 md:py-32 overflow-hidden">
      <div className="container mx-auto px-4 md:px-6">
        <div className="mb-12 flex flex-col md:flex-row md:items-end justify-between gap-6 border-b border-border pb-12">
          <div className="max-w-2xl">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-normal font-default text-foreground mb-4 tracking-tight">
              Curated Wellness Essentials
            </h2>
            <p className="text-muted-foreground text-base md:text-lg font-default leading-relaxed max-w-xl">
              Elevate your daily ritual with our premium selection of clinical-grade supplements and recovery tools, hand-selected for the M / O Protocol.
            </p>
          </div>
          <div className="shrink-0">
            <Link to="#" className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-xs uppercase tracking-widest font-medium transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 border border-input bg-background hover:bg-primary hover:text-primary-foreground h-12 px-8 py-2 shadow-sm">
              Shop All Products
            </Link>
          </div>
        </div>

        <ShopifyProductsProvider domain="moprotocol.myshopify.com" limit={8}>
          <FeaturedProductsCarousel />
        </ShopifyProductsProvider>
      </div>
    </section>;
}